import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { workspaceReceiptSenderContract } from "@common/contracts/workspace-receipt-sender.contract";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const workspaceReceiptSenderContractClient = getBackendAPIClient(
    workspaceReceiptSenderContract,
);

const workspaceReceiptSenderQueryKeys = {
    all: () => ["workspace-receipt-sender"],
    workspace: (workspaceId: string) => [
        ...workspaceReceiptSenderQueryKeys.all(),
        workspaceId,
    ],
} as const;

export function useWorkspaceReceiptSenders() {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: workspaceId
            ? workspaceReceiptSenderQueryKeys.workspace(workspaceId)
            : [],
        queryFn: async () => {
            if (!workspaceId) {
                return [];
            }

            const result =
                await workspaceReceiptSenderContractClient.getForWorkspace({
                    params: {
                        workspaceId,
                    },
                });

            return result.body.receiptSenders;
        },
        enabled: !!workspaceId,
    });
}

export function useCreateWorkspaceReceiptSenderEmail() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: {
            email: string;
            verificationCode: string;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const result =
                await workspaceReceiptSenderContractClient.createEmail({
                    params: {
                        workspaceId,
                    },
                    body: {
                        email: data.email,
                        verificationCode: data.verificationCode,
                    },
                });

            return result.body;
        },
        onMutate: async (data) => {
            if (!workspaceId) {
                return;
            }

            const queryKey =
                workspaceReceiptSenderQueryKeys.workspace(workspaceId);

            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey });

            // Snapshot previous value
            const previousData = queryClient.getQueryData(queryKey);

            // Optimistically update
            queryClient.setQueryData(queryKey, (old: any[] = []) => [
                {
                    email: data.email,
                    workspaceId,
                    isVerified: false,
                    limit: 500,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                },
                ...old,
            ]);

            return { previousData, queryKey };
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.queryKey && context?.previousData !== undefined) {
                queryClient.setQueryData(
                    context.queryKey,
                    context.previousData,
                );
            }
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey:
                        workspaceReceiptSenderQueryKeys.workspace(workspaceId),
                });
            }
        },
    });
}

export function useCreateWorkspaceReceiptSenderPhoneNumber() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: {
            phoneNumber: string;
            verificationCode: string;
        }) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const result =
                await workspaceReceiptSenderContractClient.createPhoneNumber({
                    params: {
                        workspaceId,
                    },
                    body: {
                        phoneNumber: data.phoneNumber,
                        verificationCode: data.verificationCode,
                    },
                });

            return result.body;
        },
        onMutate: async (data) => {
            if (!workspaceId) {
                return;
            }

            const queryKey =
                workspaceReceiptSenderQueryKeys.workspace(workspaceId);

            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey });

            // Snapshot previous value
            const previousData = queryClient.getQueryData(queryKey);

            // Optimistically update
            queryClient.setQueryData(queryKey, (old: any[] = []) => [
                {
                    phoneNumber: data.phoneNumber,
                    workspaceId,
                    limit: 500,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                },
                ...old,
            ]);

            return { previousData, queryKey };
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.queryKey && context?.previousData !== undefined) {
                queryClient.setQueryData(
                    context.queryKey,
                    context.previousData,
                );
            }
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey:
                        workspaceReceiptSenderQueryKeys.workspace(workspaceId),
                });
            }
        },
    });
}

export function useRemoveWorkspaceReceiptSender() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            await workspaceReceiptSenderContractClient.remove({
                params: {
                    workspaceId,
                    id,
                },
                body: {},
            });
        },
        onMutate: async (id) => {
            if (!workspaceId) {
                return;
            }

            const queryKey =
                workspaceReceiptSenderQueryKeys.workspace(workspaceId);

            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey });

            // Snapshot previous value
            const previousData = queryClient.getQueryData(queryKey);

            // Optimistically remove
            queryClient.setQueryData(queryKey, (old: any[] = []) =>
                old.filter((sender) => sender.id !== id),
            );

            return { previousData, queryKey };
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.queryKey && context?.previousData !== undefined) {
                queryClient.setQueryData(
                    context.queryKey,
                    context.previousData,
                );
            }
        },
        onSuccess: () => {
            if (workspaceId) {
                queryClient.invalidateQueries({
                    queryKey:
                        workspaceReceiptSenderQueryKeys.workspace(workspaceId),
                });
            }
        },
    });
}
